import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as LogoIcon } from '@src/assets/icons/logo.svg';
import { NavLink, useNavigate } from 'react-router-dom';
import { Button } from 'antd';
import { ROUTE_SUPPORT } from '@src/constants/URLS.const';
import AuthMiddleImage from '@src/assets/images/auth-middle.png';
import AuthLogoImage from '@src/assets/images/atuh-logo.png';
import { AdjustmentsVerticalIcon } from '@heroicons/react/24/solid';
import { ReactComponent as SimCard } from '@src/assets/icons/simCard.svg';

type AuthWrapperProps = {
    pageTitle?: string;
    subtitle?: string;
    children?: ReactNode;
    footer?: ReactNode;
};

export function AuthWrapper(props: AuthWrapperProps) {
    const { pageTitle, subtitle, children, footer } = props;
    const { t } = useTranslation();
    const navigate = useNavigate();

    return (
        <main className="h-screen w-full bg-cover flex justify-center md:justify-end">
            <div className="hidden md:flex relative w-full md:w-1/2 lg:w-3/5 bg-[#cdd8f9]">
                <img
                    src={AuthLogoImage}
                    alt="Background logo"
                    className="absolute w-full h-full object-contain z-0"
                />
                <div
                    className="flex items-center gap-2 absolute left-12 top-8 text-blue-600 cursor-pointer"
                    onClick={() => navigate('/')}
                >
                    <LogoIcon />
                    <span className="font-medium text-4xl">CMP</span>
                </div>

                <div className="absolute flex flex-col items-center justify-center top-0 md:right-5 lg:right-10 h-64 w-60 bg-white rounded-b-[42px] shadow-lg">
                    <AdjustmentsVerticalIcon
                        height={75}
                        width={75}
                        className="text-gray-600"
                    />
                    <p className="font-['Plus_Jakarta_Sans'] font-bold text-[20px] leading-[150%] tracking-[0px] text-center text-black-primary">
                        Plan management
                    </p>
                </div>

                <div className="absolute flex flex-col items-center justify-center bottom-5 lg:bottom-10 left-0 w-72 h-56 bg-white rounded-r-[42px] shadow-lg">
                    <SimCard height={75} width={75} />
                    <p className="font-['Plus_Jakarta_Sans'] font-bold text-[20px] leading-[150%] tracking-[0px] text-center text-black-primary">
                        SIM lifecycle management
                    </p>
                </div>

                <img
                    src={AuthMiddleImage}
                    alt="Middle decoration"
                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-auto max-w-[520px]  h-auto max-h-[520px] opacity-90"
                />
            </div>

            <div className="flex flex-col w-full md:w-1/2 lg:w-2/5 py-12 px-4 md:px-10 h-screen overflow-auto bg-background text-textPrimary">
                {pageTitle && (
                    <div className="text-B-32 w-full mb-1">{t(pageTitle)}</div>
                )}

                {subtitle && (
                    <div className="flex items-center w-full text-gray-400 font-normal text-base">
                        {t(subtitle)}
                        <NavLink to={`/${ROUTE_SUPPORT}`} className="pl-2">
                            <Button className="btn-tertiary border-none">
                                Get Support
                            </Button>
                        </NavLink>
                    </div>
                )}

                <div className="flex-1 flex items-center justify-center w-full">
                    <div className="w-full">{children}</div>
                </div>
                <div className="mt-auto w-full">{footer}</div>
            </div>
        </main>
    );
}
